import React from "react";
import { BusinessHoursSchedule } from "@/model/interface/CallFlow";
import { WEEK_DAYS } from "@/util/callFlowNodes";
import { clockText, shortDayName, zonedNow } from "@/util/availability";
import { cn } from "@/lib/utils";

interface WorkingHoursWeekProps {
  schedule: BusinessHoursSchedule;
  timeZone: string;
}

const FIRST_MINUTE = 6 * 60;
const LAST_MINUTE = 22 * 60;
const END_OF_DAY_MINUTE = 24 * 60;
const AXIS_TIMES = ["09:00", "12:00", "15:00", "18:00"];

function minutesOf(time: string): number {
  const [hourText, minuteText] = time.split(":");
  return Number(hourText) * 60 + Number(minuteText);
}

function percentOf(minutes: number): number {
  const clamped = Math.min(Math.max(minutes, FIRST_MINUTE), LAST_MINUTE);
  return ((clamped - FIRST_MINUTE) / (LAST_MINUTE - FIRST_MINUTE)) * 100;
}

export default function WorkingHoursWeek({ schedule, timeZone }: WorkingHoursWeekProps) {
  const now = zonedNow(timeZone);
  const isNowVisible = now.minutes >= FIRST_MINUTE && now.minutes <= LAST_MINUTE;

  return (
    <div className="overflow-x-auto">
      <div className="grid min-w-[300px] grid-cols-[40px_repeat(7,minmax(30px,1fr))] gap-1 text-[11px]">
        <span />
        {WEEK_DAYS.map((day) => (
          <span
            key={day}
            className={cn("text-center font-bold", day === now.weekDay ? "text-primary" : "text-muted-foreground")}
          >
            {shortDayName(day)}
          </span>
        ))}
        <div className="relative h-[150px] text-[10.5px] tabular-nums text-muted-foreground">
          {AXIS_TIMES.map((time) => (
            <span
              key={time}
              className="absolute right-1 -translate-y-1/2 whitespace-nowrap"
              style={{ top: `${percentOf(minutesOf(time))}%` }}
            >
              {clockText(time)}
            </span>
          ))}
        </div>
        {WEEK_DAYS.map((day) => (
          <div key={day} className="relative h-[150px] overflow-hidden rounded-md bg-muted">
            {(schedule.days?.[day] ?? []).map((range, rangeIndex) => {
              const startMinute = minutesOf(range.start);
              const endMinute = minutesOf(range.end) <= startMinute ? END_OF_DAY_MINUTE : minutesOf(range.end);
              return (
                <span
                  key={`${day}-${rangeIndex}`}
                  className="absolute inset-x-[3px] rounded border border-success/45 bg-success-soft"
                  style={{
                    top: `${percentOf(startMinute)}%`,
                    height: `${percentOf(endMinute) - percentOf(startMinute)}%`,
                  }}
                />
              );
            })}
            {day === now.weekDay && isNowVisible && (
              <span className="absolute inset-x-0 h-0.5 bg-destructive" style={{ top: `${percentOf(now.minutes)}%` }} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
